import {Component, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {AuthService} from '../../service/auth.service';
import {LocalStorageService} from 'ngx-webstorage';
import {UrlHelper} from '../../helper/url.helper';
import {localizeRole, Role} from '../../model/auth/Role';
import {LocalStorageKeys} from '../../helper/local-storage.keys';
import {NotificationHandler} from '../../helper/notification.handler';
import {LanguageSwitchComponent} from '../../common/language-switch/language-switch.component';
import {Competition} from '../../model/competition/Competition';
import * as Sentry from '@sentry/angular';
import {environment} from '../../../environments/environment';
import smartlookClient from 'smartlook-client';

@Component({
  selector: 'role-select',
  templateUrl: './role-select.component.html',
  styleUrls: ['./role-select.component.scss']
})
export class RoleSelectComponent implements OnInit {

  roleField = new FormControl('', [
    Validators.required
  ]);

  roleSelectForm: FormGroup = new FormGroup({
    role: this.roleField,
  });

  roles?: Role[];

  returnUrl?: string;

  private readonly localeOverride;

  constructor(private router: Router,
              private authService: AuthService,
              private localStorageService: LocalStorageService,
              @Inject(LOCALE_ID) private locale: string,
              route: ActivatedRoute) {
    this.localeOverride = route.snapshot.queryParamMap.get(LanguageSwitchComponent.MANUAL_LOCALE_PARAM) === 'true';
    this.returnUrl = decodeURI(route.snapshot.queryParams.returnUrl as string || '');
  }

  async ngOnInit() {
    if (!this.localeOverride) {
      const user = await this.authService.getUser();
      if (user && user.locale && user.locale !== this.locale) {
        LanguageSwitchComponent.switchLanguage(user.locale as string);
      }
    }

    const session = await this.authService.getSession();

    if (session.roles && session.roles.length > 0) {
      if (session.roles.length > 1) {
        this.roles = session.roles;
      } else {
        await this.setSelectedRole(session.roles[0]);
      }
    } else {
      await this.signOut();
    }
  }

  compareRoles(r1: Role, r2: Role) {
    return r1 == r2;
  }

  displayRole(role: Role) {
    return localizeRole(role);
  }

  async selectRole(change: any) {
    await this.setSelectedRole(change as Role);
  }

  private async setSelectedRole(role: Role) {
    if (role != Role.Admin) {
      const tenant = this.localStorageService.retrieve(LocalStorageKeys.SESSION_TENANT) as Competition;
      if (!tenant) {
        // await this.signOut();
        // return;
        role = Role.Participant; // in case this is not an Admin and no tenant info is present set Participant role
      }
    }

    this.localStorageService.store(LocalStorageKeys.SESSION_ROLE, role);

    const params: { [key: string]: string } = {};
    if (this.localeOverride) {
      params[LanguageSwitchComponent.MANUAL_LOCALE_PARAM] = String(this.localeOverride);
    }

    await this.router.navigate([this.returnUrl || UrlHelper.DEFAULT_AUTH_PATH], {
      replaceUrl: true,
      queryParams: params
    });
  }

  async signOut() {
    try {
      await this.authService.logout();
      this.localStorageService.clear();
      Sentry.configureScope(scope => scope.setUser(null));
      if (environment.smartlook.enabled) smartlookClient.anonymize();
    } catch (e) {
      NotificationHandler.handleError(e);
    } finally {
      await this.router.navigate([UrlHelper.SIGN_IN_PATH], {replaceUrl: true});
    }
  }
}
